import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const MobileNavigationMenu = ({
  isOpen,
  onClose,
  isAuthenticated,
  isAdmin,
  onLogout
}) => {
  const navigate = useNavigate();

  const handleNavigation = (path, state = {}) => {
    navigate(path, { state });
    onClose();
  };

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY;

      // Prevent body scroll
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      return () => {
        // Restore body scroll
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';

        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div 
      id="mobile-menu"
      className="md:hidden fixed top-16 left-0 right-0 bottom-0 bg-white border-t border-gray-200 shadow-lg z-40 overflow-y-auto"
      role="navigation"
      aria-label="Mobile navigation"
    >
      <div className="px-2 pt-2 pb-6 space-y-1">

        {/* Browse Section */}
        <div className="pt-2 pb-3">
          <h3 className="px-3 text-xs font-semibold text-[#9256A0] uppercase tracking-wider mb-2">
            Browse
          </h3>
          <Link
            to="/"
            className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150"
            onClick={onClose}
          >
            Home
          </Link>
          <Link
            to="/businesses"
            className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150"
            onClick={onClose}
          >
            Businesses
          </Link>
          <Link
            to="/events"
            className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150"
            onClick={onClose}
          >
            Events
          </Link>
          <Link
            to="/resources"
            className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150"
            onClick={onClose}
          >
            Resources
          </Link>
          <Link
            to="/blog"
            className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150"
            onClick={onClose}
          >
            Blog
          </Link>
        </div>

        {/* Add Listing Section */}
        <div className="pt-2 pb-3 border-t border-gray-200">
          <h3 className="px-3 text-xs font-semibold text-[#4BB0A8] uppercase tracking-wider mb-2">
            {isAuthenticated ? 'Add Listing' : 'Suggest Content'}
          </h3>

          {isAuthenticated ? (
            // Logged-in users see all options
            <>
              <button
                onClick={() => handleNavigation('/submit-business')}
                className="w-full text-left px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150 flex items-center"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#4BB0A8] mr-3">
                  <path d="M3 21H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 21V7L13 3V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M19 21V12L13 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Submit Business
              </button>
              <button
                onClick={() => handleNavigation('/submit-event')}
                className="w-full text-left px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150 flex items-center"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#4BB0A8] mr-3">
                  <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Submit Event
              </button>
              <button
                onClick={() => handleNavigation('/submit-resource')}
                className="w-full text-left px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150 flex items-center"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#4BB0A8] mr-3">
                  <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 7H7V16H10V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 7H14V12H17V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Submit Resource
              </button>
              <button
                onClick={() => handleNavigation('/submit-blog')}
                className="w-full text-left px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150 flex items-center"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#4BB0A8] mr-3">
                  <path d="M12 5H5C3.89543 5 3 5.89543 3 7V19C3 20.1046 3.89543 21 5 21H17C18.1046 21 19 20.1046 19 19V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17 3C17.5304 3 18.0391 3.21071 18.4142 3.58579C18.7893 3.96086 19 4.46957 19 5C19 5.53043 18.7893 6.03914 18.4142 6.41421C18.0391 6.78929 17.5304 7 17 7C16.4696 7 15.9609 6.78929 15.5858 6.41421C15.2107 6.03914 15 5.53043 15 5C15 4.46957 15.2107 3.96086 15.5858 3.58579C15.9609 3.21071 16.4696 3 17 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 11H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Submit Blog Post
              </button>
            </>
          ) : (
            // Non-logged-in users see limited options with login prompt
            <>
              <button
                onClick={() => handleNavigation('/suggest-listing')}
                className="w-full text-left px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150 flex items-center"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#4BB0A8] mr-3">
                  <path d="M3 21H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 21V7L13 3V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M19 21V12L13 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Suggest a Business
              </button>

              <div className="px-3 py-3 mt-2 border-t border-gray-200">
                <p className="text-xs text-gray-600 mb-3">
                  Want to list your business, event, resource, or blog?
                </p>
                <p className="text-xs font-medium text-gray-700 mb-2">
                  Create an account or login
                </p>
              </div>
            </>
          )}
        </div>

        {/* Tools Section */}
        <div className="pt-2 pb-3 border-t border-gray-200">
          <h3 className="px-3 text-xs font-semibold text-[#4BB0A8] uppercase tracking-wider mb-2">
            Tools
          </h3>

          {/* Family Tools */}
          <p className="px-3 text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1 mt-2">
            For Families
          </p>
          <Link
            to="/kindlyme"
            className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150"
            onClick={onClose}
          >
            KindlyMe Card
          </Link>
          <Link
            to="/tools/iep-meeting-prep"
            className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150"
            onClick={onClose}
          >
            IEP/504 Meeting Prep
          </Link>
          <Link
            to="/tools/doctor-visit-prep"
            className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150"
            onClick={onClose}
          >
            Doctor Visit Prep
          </Link>
          <Link
            to="/tools/visit-prep-kit"
            className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150"
            onClick={onClose}
          >
            Visit Prep Kit
          </Link>
          <Link
            to="/tools/story-creator"
            className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150"
            onClick={onClose}
          >
            Story Creator
          </Link>

          {/* Guides */}
          <p className="px-3 text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1 mt-3">
            Guides
          </p>
          <Link
            to="/guides/first-90-days"
            className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150"
            onClick={onClose}
          >
            First 90 Days After Diagnosis
          </Link>

          {/* Business Tools */}
          <p className="px-3 text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1 mt-3">
            For Businesses
          </p>
          <Link
            to="/tools/accessibility-assessment"
            className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#9256A0] hover:bg-gray-50 transition-colors duration-150"
            onClick={onClose}
          >
            Accessibility Self-Assessment
          </Link>
          <Link
            to="/tools/staff-training"
            className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#9256A0] hover:bg-gray-50 transition-colors duration-150"
            onClick={onClose}
          >
            Staff Training Guide
          </Link>
        </div>

        {/* My Account Section (only when authenticated) */}
        {isAuthenticated && (
          <div className="pt-2 pb-3 border-t border-gray-200">
            <h3 className="px-3 text-xs font-semibold text-[#9256A0] uppercase tracking-wider mb-2">
              My Account
            </h3>
            <Link
              to="/profile"
              className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150"
              onClick={onClose}
            >
              Dashboard
            </Link>
            <Link
              to="/saved-items"
              className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150"
              onClick={onClose}
            >
              Saved Items
            </Link>
            {isAdmin && (
              <Link
                to="/admin"
                className="block px-3 py-2.5 rounded-md text-base font-medium text-gray-700 hover:text-[#4BB0A8] hover:bg-gray-50 transition-colors duration-150"
                onClick={onClose}
              >
                Admin Dashboard
              </Link>
            )}
          </div>
        )}

        {/* Auth Section */}
        <div className="pt-4 pb-3 border-t border-gray-200">
          {isAuthenticated ? (
            <button
              onClick={() => {
                onLogout();
                onClose();
              }}
              className="w-full text-left px-3 py-2.5 rounded-md text-base font-medium text-red-600 hover:bg-red-50 transition-colors duration-150"
            >
              Logout
            </button>
          ) : (
            <div className="flex items-center px-4 space-x-3">
              <button
                onClick={() => handleNavigation('/login')}
                className="flex-1 px-4 py-2.5 text-center text-[#4BB0A8] border border-[#4BB0A8] rounded-md hover:bg-[#4BB0A8] hover:text-white transition-colors duration-200 font-medium"
              >
                Login
              </button>
              <button
                onClick={() => handleNavigation('/register')}
                className="flex-1 px-4 py-2.5 text-center bg-gradient-to-r from-[#4BB0A8] to-[#9256A0] text-white rounded-md hover:opacity-90 transition-opacity duration-200 font-medium"
              >
                Register
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileNavigationMenu;
